'use client';
import { getOwnRelease } from '@/helper/ownRelease';
import { useEffect, useState } from 'react';

// =========================================================
// Chunk-error version check
// =========================================================
//
// Shared helper for `error.tsx` and `global-error.tsx`. When the
// router surfaces an error to a boundary, we need to answer one
// question: is this the symptom of a deploy replacing our chunk
// files, or is it something else (network blip, runtime exception,
// unexpected server response, …)?
//
// The answer matters because the remediation differs:
//   - Deploy replaced our chunks → only a hard browser reload
//     recovers. Soft nav / `reset()` re-triggers the same failure.
//   - Anything else → the existing `ErrorLayout` with its countdown
//     and `reset()` button is correct.
//
// We distinguish by probing /api/version and comparing the reported
// release to the release this bundle was built from. A different value
// means a new deploy is live; any other outcome (same value, probe
// failed, probe timed out, or a build that does not know its own
// release) is explicitly *not* evidence of a deploy and we fall through
// to the general error layout.

const PROBE_TIMEOUT_MS = 3_000;

export type ChunkVersionCheckState = 'probing' | 'version-changed' | 'not-applicable';

// Runtime extraction of the `release` string from an arbitrary JSON
// payload. Narrowing (no type assertions) to satisfy the project's
// `no-type-assertion` ESLint rule and to defensively handle any
// malformed response shape.
const extractRelease = (parsed: unknown): string => {
  if (typeof parsed !== 'object' || parsed === null) {
    return '';
  }
  if (!('release' in parsed)) {
    return '';
  }
  const release = parsed.release;
  return typeof release === 'string' ? release : '';
};

// Matches webpack's ChunkLoadError as well as Next.js's dynamic-import
// failure surface. Covers both `error.name` (the standard identifier)
// and the human-readable message, since intermediate wrappers can
// rename the error but usually preserve the message.
function isChunkError(error: Error): boolean {
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = error.message ?? '';
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

async function probeForDeployChange(): Promise<boolean> {
  const ownRelease = getOwnRelease();
  if (ownRelease === '') {
    // A build made without a release build-arg cannot tell its own
    // chunks from a newer deploy's, so it makes no claim.
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`/api/version?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) {
      return false;
    }
    const release = extractRelease(await response.json());
    return release !== '' && release !== ownRelease;
  } catch {
    // Timeout, network error, JSON parse failure — none of these
    // are confirmation of a deploy. Fall through.
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function useChunkErrorVersionCheck(error: Error): ChunkVersionCheckState {
  const [state, setState] = useState<ChunkVersionCheckState>(() =>
    isChunkError(error) ? 'probing' : 'not-applicable',
  );

  useEffect(() => {
    if (state !== 'probing') {
      return;
    }
    let cancelled = false;
    void probeForDeployChange().then((changed) => {
      if (!cancelled) {
        setState(changed ? 'version-changed' : 'not-applicable');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  return state;
}
